import Inputs from "./Inputs";
import HTTP from "./HTTP";
import { isEmpty, isArray, extend, forOwn, find, reduce, indexOf } from 'lodash-es';

export default class Form {

	/**
	 * Constructs a form layout (multi-page if needed) and embeds it into a modal
	 * This can be used as a standalone feature
	 * @param options
	 * @param modal Extension
	 */
	constructor (options, modal) {
		let self = this;
		self.schema = [];
		self.schemaValues = [];

		extend(self, options);

		self.formIndex = 0;
		self.inputs = [];
		self.footerInputs = {};
		self.whiteListErrors = [];
		self.footerActions = {
			actions: {
				next: {
					text: 'Next',
					type: 'button',
					classes: ['action', 'hide']
				},
				previous: {
					text: 'Previous',
					type: 'button',
					classes: ['action', 'negative', 'hide']
				},
				submit: {
					text: 'Submit',
					type: 'submit',
					classes: ['action', 'hide']
				}
			}
		};
		this.modal = modal;

		if (options.footerActions) {
			extend(self.footerActions.actions, options.footerActions.actions);
			extend(self.footerActions, {details: (options.footerActions.details || '')});
		}

		this.modal.footerActions(self.footerActions);
		self.formFooterActionsIndex();

		self.form = document.createElement('form');

		self.formReadyEvent = document.createEvent("Event");
		self.formReadyEvent.initEvent("dataReady", true, false);

		self.form.inputEvents = document.createEvent("Event");
		self.form.inputEvents.initEvent("bindinputs", true, false);

		self.validateInputs = document.createEvent("Event");
		self.validateInputs.initEvent("validateInputs", true, false);

		self.schema.forEach((schema, key) => {
			self.formSection = document.createElement('div');
			self.formSection.classList.add('form-section');

			self.formContainer = document.createElement('ul');
			self.formContainer.classList.add('flex-outer');

			this.addSchema(schema, key);

			self.formSection.appendChild(self.formContainer);
			self.form.appendChild(self.formSection);
		});

		self.modal.footerActionInputs.previous.addEventListener('click', this.changePage.bind(this, 'prev'));
		self.modal.footerActionInputs.next.addEventListener('click', this.changePage.bind(this));

		self.form.childNodes[0].classList.add('current');
		self.form.dispatchEvent(self.formReadyEvent);
	}

	/**
	 *
	 * @param schema
	 * @param key
	 */
	addSchema(schema, key) {
		schema.forEach((newInput) => {
			this.formList = document.createElement('li');
			let input;

			if (newInput.type === 'hidden' &&  isArray(newInput.value) && !isEmpty(newInput.value)) {
				newInput.value.forEach((val) => {
					newInput.value = val;
					newInput.multiple = true;
					input = new Inputs(newInput, this);
					input.schemaKey = key;
					this.inputs.push(input);
					this.formList.appendChild(input.input);
				});
			} else {
				input = new Inputs(newInput, this);
				input.schemaKey = key;
				this.inputs.push(input);
			}

			input.input.addEventListener('keyup', input.bindValue.bind(input));
			input.input.addEventListener('change', input.bindValue.bind(input));
			this.form.addEventListener('bindinputs', input.bindValue.bind(input));

			if (input.formLabel && input.type !== 'hidden') {
				this.formList.appendChild(input.formLabel);
			}

			this.formList.setAttribute('data-visibility', input.name);
			this.formList.appendChild(input.input);

			if (input.validation || input.type === 'email') {
				input.addErrorSpan();
				this.formList.appendChild(input.inputError);
				input.input.addEventListener('change', input.validateInput.bind(input));
				input.input.addEventListener('keyup', input.validateInput.bind(input));
				this.form.addEventListener('validateInputs', input.validateInput.bind(input));
				this.modal.footerActionInputs.next.addEventListener('click', input.validateInput.bind(input));
			}

			if (input.visibility) {
				input.input.addEventListener('change', this.visibility.bind(this, input));
				input.input.addEventListener('keyup', this.visibility.bind(this, input));
				this.form.addEventListener('dataReady', this.visibility.bind(this, input));
			}

			if (newInput.liClasses) {
				this.addClasses(this.formList, newInput.liClasses);
			}

			this.formContainer.appendChild(this.formList);
		});
	}

	/**
	 * Increases the index value - used to keep track of the form page number
	 */
	next () {
		this.formIndex++;
	}

	/**
	 * Decreases the index value - used to keep track of the form page number
	 */
	previous () {
		this.formIndex--;
	}

	/**
	 * Determines the current index and the direction selected so it knows which page of the form to turn to
	 *
	 * @param index
	 * @param direction
	 */
	navigateFormSection (index, direction) {
		if (index > 0) {
			this.form.childNodes[(index - 1)].classList.remove('current');
		}

		if (direction === 'prev') {
			this.form.childNodes[(index + 1)].classList.remove('current');
		}

		this.form.childNodes[index].classList.add('current');
	}

	/**
	 * Determines which buttons to show depending on the form index (next/previous/submit etc)
	 */
	formFooterActionsIndex () {
		let index = this.formIndex;
		let formLength = (this.schema.length - 1);
		let prevButton = this.modal.footerActionInputs.previous;
		let nextButton = this.modal.footerActionInputs.next;
		let submitButton = this.modal.footerActionInputs.submit;

		if (index > 0) {
			prevButton.classList.remove('hide');
		}

		if (index <= 0) {
			prevButton.classList.add('hide');
		}

		if (index !== formLength) {
			submitButton.classList.add('hide');
			nextButton.classList.remove('hide');
		}

		if (index === formLength) {
			nextButton.classList.add('hide');
			submitButton.classList.remove('hide');
		}
	}

	/**
	 * Changes the page depending on the direction/current index, return false/true will halt if there
	 * are any errors (validation or otherwise on the form)
	 * @param direction previous/next
	 * @returns {boolean}
	 */
	changePage (direction) {
		let errorsFound = this.checkFormErrors();

		if (errorsFound.length > 0 && direction !== 'prev') {
			return false;
		}

		if (direction !== 'prev') {
			this.next();
		} else {
			this.previous();
		}

		this.formFooterActionsIndex();
		this.navigateFormSection(this.formIndex, direction);
		this.removeSpanErrors();

		return true;
	}

	/**
	 * Removes the current errors found on each page, so that when changing the page it'll remove them
	 * And re-checks for errors as the user attempt to change the page again (looks cleaner)
	 */
	removeSpanErrors () {
		let spanElements = this.form.querySelectorAll('span.error');

		forOwn(spanElements, (ele) => {
			if (ele.hasChildNodes()){
				ele.removeChild(ele.lastChild);
			}
		});
	}

	/**
	 * Checks the form inputs for any validation errors (required, minlength, maxlength, email etc)
	 *
	 * @returns An array of errors/empty array
	 */
	checkFormErrors () {
		let formObj = this;

		return reduce(this.inputs, (errors, formInput) => {
			if ((formInput.validationMessage !== undefined) && !isEmpty(formInput.validationMessage)
				&& formInput.schemaKey === formObj.formIndex) {
				return errors.concat(formInput.validationMessage);
			}

			return errors;
		}, []);
	}

	/**
	 * Form footer actions have the option to have an ajax submission (post or get)
	 * This will automatically generate the form data, do the HTTP request
	 * and then pass back the response to the onSuccess/onFailure callback
	 *
	 * @param inputObj Footer actions are generated using the inputs object and so has the callbacks attached to it.
	 * Examples in the readme on how to use this.
	 */
	formAjaxHandler (inputObj) {
		let formObj = this;
		formObj.form.dispatchEvent(formObj.validateInputs);

		let errorsFound = this.checkFormErrors();

		if (!inputObj.ignoreValidation && (errorsFound.length > 0 || this.whiteListErrors.length > 0)) {
			return false;
		} else {
			let ajaxObj = {
				url: inputObj.ajax.url,
				timeout: 5000,
				onSuccess: (responseData) => {
					inputObj.ajax.onSuccess(responseData);
				},
				onFailure: (status, callbackData, responseText) => {
					inputObj.ajax.onFailure(status, callbackData, responseText);
				}
			};

			if (inputObj.ajax.type === 'post') {
				let formData = new FormData(this.form);

				if (inputObj.ajax.data !== undefined) {
					for(let entry of inputObj.ajax.data.entries()) {
						formData.append(entry[0], entry[1]);
					}
				}

				extend(ajaxObj, {data: formData});
			}

			let request = new HTTP();

			request[inputObj.ajax.type](ajaxObj);

			return true;
		}
	}

	/**
	 * Hides/Unhides given elements on a form based on the values assigned
	 * @param inputObj The inputs object
	 */
	visibility (inputObj) {
		let targets = inputObj.visibility.target;
		let unHideValues = inputObj.visibility.unHide;
		let formInputs = this.inputs;

		if (typeof unHideValues === 'function') {
			unHideValues = unHideValues();
		}

		targets.forEach((target) => {
			let targetObj = find(formInputs, {name: target});

			if (indexOf(unHideValues, inputObj.newValue) >= 0 ||
				indexOf(unHideValues, inputObj.value[inputObj.newValue]) >= 0
			) {
				targetObj.input.parentNode.style.display = '';

				if (targetObj.newValue) {
					targetObj.input.value = targetObj.newValue;
				}
			} else {
				targetObj.input.parentNode.style.display = 'none';
				targetObj.input.value = '';
			}
		});
	}

	/**
	 * Adds a list of class names to a given input
	 * @param ele The input target
	 * @param classes Array of class names
	 */
	addClasses (ele, classes) {
		if (!isArray(classes)) {
			throw new Error ('Classes needs defining as an array of class names');
		}

		classes.forEach((val) => {
			ele.classList.add(val);
		});
	}

}
